"use client";
import { useCallback, useState, useMemo } from "react";
import Image from "next/image";
import styles from "./index.module.scss";

import { ReportParentContentEnum } from "@/app/types/typeConfig/report";
import { IMConfigTypeEnum } from "@/app/types/typeConfig/chatQuestion";
import { useTranslations } from "@/app/lib/LocaleProvider";
import { useMessageStore } from "@/app/store";
import useAIBehaviorCollect from "@/app/hooks/robot/useAIBehaviorCollect";
import { createUuid, copyToClipboard } from "@/app/utils/string";
import {
  HeaderType,
  MessageSender,
  MessageType,
  RobotMessageType,
} from "@/app/store/types";
import { Toast } from "@douyinfe/semi-ui";
interface TechAIFooterProps {
  uniqueKey?: string;
  data: {
    parentContentInfo?: {
      parentContentId?: string;
      parentContent?: string;
      parentContentType?: ReportParentContentEnum;
    };
    [key: string]: unknown;
  };
  requestId?: string;
  content?: string; // 消息内容，用于复制
  onStop?: () => void;
  onRedo?: () => void;
  onLike?: () => void;
  onDislike?: () => void;
}

/**
 * 反馈状态类型
 */
type FeedbackState = "none" | "liked" | "disliked";

/**
 * AI 消息底部栏组件
 * 提供 AI 回复的状态提示和用户反馈功能（点赞/点踩/停止）
 */
export default function FeedFooter({
  data,
  requestId,
  content,
  onLike,
  onDislike,
}: TechAIFooterProps) {
  const { setRobotMessage } = useMessageStore();
  const { onLikeCollect, onDislikeCollect } = useAIBehaviorCollect();
  const t = useTranslations();
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("none");
  const parentContentInfo = data?.parentContentInfo || {};
  // 计算图标 URL
  const iconUrls = useMemo(
    () => ({
      like:
        feedbackState === "liked"
          ? "/images/like2-visited.svg"
          : "/images/like2.svg",
      dislike:
        feedbackState === "disliked"
          ? "/images/dislike2-visited.svg"
          : "/images/dislike2.svg",
    }),
    [feedbackState],
  );

  // 处理点踩
  const handleDislike = useCallback(() => {
    if (feedbackState !== "none") return;
    setFeedbackState("disliked");
    onDislikeCollect(
      {
        parentContentId: parentContentInfo.parentContentId || "",
        parentContent: parentContentInfo.parentContent || "",
        parentContentType:
          (parentContentInfo.parentContentType as ReportParentContentEnum) ||
          ReportParentContentEnum.DEFAULT,
      },
      requestId,
    );
    setRobotMessage({
      content: "",
      timestamp: Date.now(),
      streaming: false,
      clientId: createUuid(),
      type: MessageType.Text,
      headerType: HeaderType.MESSAGE,
      senderType: MessageSender.ROBOT,
      messageType: RobotMessageType.ROBOT_FEEDBACK,
      senderName: "智能助手",
      robotData: {
        feedback: "dislike",
        requestId: requestId,
        parentContentInfo,
        configType: IMConfigTypeEnum.generalFAQ,
      },
    });
    onDislike?.();
  }, [feedbackState, onDislike, requestId]);

  // 处理复制
  const handleCopy = useCallback(async () => {
    if (!content) return;
    // 将 HTML 内容放到临时 div 中，提取纯文本
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    if (!textContent.trim()) return;
    const success = await copyToClipboard(textContent);
    if (success) {
      Toast.success(t.tips.copySuccess);
    }
  }, [content, t.tips.copySuccess]);

  // 处理点赞
  const handleLike = useCallback(() => {
    if (feedbackState !== "none") return;
    setFeedbackState("liked");
    onLikeCollect(
      {
        parentContentId: (parentContentInfo?.parentContentId as string) || "",
        parentContent: (parentContentInfo?.parentContent as string) || "",
        parentContentType:
          (parentContentInfo?.parentContentType as ReportParentContentEnum) ||
          ReportParentContentEnum.DEFAULT,
      },
      requestId,
    );
    setRobotMessage({
      content: "",
      timestamp: Date.now(),
      streaming: false,
      clientId: createUuid(),
      type: MessageType.Text,
      headerType: HeaderType.MESSAGE,
      senderType: MessageSender.ROBOT,
      messageType: RobotMessageType.ROBOT_FEEDBACK,
      senderName: "智能助手",
      robotData: {
        feedback: "like",
        requestId: requestId,
        parentContentInfo,
        configType: IMConfigTypeEnum.generalFAQ,
      },
    });

    onLike?.();
  }, [feedbackState, onLike, requestId]);

  // 获取反馈按钮的 className
  const getFeedbackClassName = useCallback(
    (type: "like" | "dislike") => {
      const baseClass = type === "like" ? styles.like : styles.dislike;
      if (feedbackState === "none") return baseClass;

      const isActive =
        (type === "like" && feedbackState === "liked") ||
        (type === "dislike" && feedbackState === "disliked");

      return `${baseClass} ${isActive ? styles.active : styles.inactivated}`;
    },
    [feedbackState],
  );

  // 不显示底部栏
  return (
    <div className={styles.tips}>
      {/* 状态文本区域 */}
      <div className={styles.statusText}>
        <span>{t.chat.isHelpful}</span>
      </div>

      {/* 操作按钮区域 */}
      {
        <div className={styles.iconWrapper}>
          {
            // 打字完成显示复制/点赞/点踩按钮
            <>
              {
                <div
                  className={styles.copy}
                  onClick={handleCopy}
                  role="button"
                  tabIndex={0}
                  aria-label={t.chat.copy}
                >
                  <Image
                    src="/images/copy.svg"
                    height={20}
                    width={20}
                    alt={t.chat.copy}
                  />
                </div>
              }
              <div
                className={getFeedbackClassName("like")}
                onClick={feedbackState === "none" ? handleLike : undefined}
                role="button"
                tabIndex={0}
                aria-label="点赞"
                style={{
                  cursor: feedbackState === "none" ? "pointer" : "not-allowed",
                }}
              >
                <Image src={iconUrls.like} height={20} width={20} alt="点赞" />
              </div>
              <div
                className={getFeedbackClassName("dislike")}
                onClick={feedbackState === "none" ? handleDislike : undefined}
                role="button"
                tabIndex={0}
                aria-label="点踩"
                style={{
                  cursor: feedbackState === "none" ? "pointer" : "not-allowed",
                }}
              >
                <Image
                  src={iconUrls.dislike}
                  height={20}
                  width={20}
                  alt="点踩"
                />
              </div>
            </>
          }
        </div>
      }
    </div>
  );
}
